import { apiFetch } from './api';

/**
 * Planification d'un contrat : journées programmées, participants, et
 * sessions du calendrier proposables (les « suggérées » d'abord).
 */
export function getPlanification(contratId) {
  return apiFetch(`/contrats/${contratId}/sessions`);
}

/** Ajoute une session, depuis le calendrier (`calendrierId`) ou saisie à la main. */
export function creerSession(contratId, body) {
  return apiFetch(`/contrats/${contratId}/sessions`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/** Modification partielle : seuls les champs transmis changent. */
export function updateSession(sessionId, body) {
  return apiFetch(`/sessions-formation/${sessionId}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

/** Supprime UN document de séance : la journée, ses autres documents et ses rappels restent. */
export function supprimerDocumentSession(documentId) {
  return apiFetch(`/documents-session/${documentId}`, { method: 'DELETE' });
}

export function supprimerSession(sessionId) {
  return apiFetch(`/sessions-formation/${sessionId}`, { method: 'DELETE' });
}

/**
 * Remplace toute la liste des participants du contrat.
 *
 * `modeEnvoi` dit quoi faire des convocations déjà parties :
 *   - 'nouveaux' (défaut) : seuls les élus jamais convoqués reçoivent la leur ;
 *   - 'tous' : la convocation est renvoyée à chacun ;
 *   - 'aucun' : les documents sont regénérés, aucun email ne part.
 */
export function enregistrerParticipants(contratId, participants, modeEnvoi = 'nouveaux') {
  return apiFetch(`/contrats/${contratId}/participants`, {
    method: 'PUT',
    body: JSON.stringify({ participants, modeEnvoi }),
  });
}

/** `type` vaut 'convocation' ou 'feuille_presence'. */
export function genererDocument(sessionId, type) {
  return apiFetch(`/sessions-formation/${sessionId}/documents/${type}`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

/**
 * Envoi d'un document de séance, le serveur choisissant le message selon son
 * type :
 *   - convocation : sa page nominative à chaque participant qui a une
 *     adresse, et le document complet au contact du contrat ;
 *   - convocation du formateur : à lui seul ;
 *   - feuille de présence : le document émargé au contact du contrat.
 */
export function envoyerDocumentSession(id, body = {}) {
  return apiFetch(`/documents-session/${id}/envoyer`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * L'email du document tel qu'il partirait. Pour la convocation (plusieurs
 * destinataires), c'est le gabarit qui est rendu : les balises {{…}} seront
 * remplies pour chacun à l'envoi (`gabarit: true` dans la réponse).
 */
export function apercuEnvoiDocument(id) {
  return apiFetch(`/documents-session/${id}/envoyer/apercu`);
}

/**
 * Évaluation de fin de formation : le gabarit de l'email tel qu'il partirait
 * (avec l'évaluation et la version retenues), puis l'envoi à chaque
 * participant qui n'a pas encore répondu — chacun reçoit SON lien.
 */
export function apercuEvaluation(sessionId) {
  return apiFetch(`/sessions-formation/${sessionId}/evaluation/apercu`);
}

export function envoyerEvaluation(sessionId, { sujet, texte } = {}) {
  return apiFetch(`/sessions-formation/${sessionId}/evaluation/envoyer`, {
    method: 'POST',
    body: JSON.stringify({ sujet, texte }),
  });
}

/**
 * Attestations : le gabarit de l'email de signature tel qu'il partirait
 * (balises {{contact}} et {{lien}} comprises), puis l'envoi à chaque
 * participant qui n'a pas encore signé — chacun reçoit SON lien.
 */
export function apercuSignatureAttestation(documentId) {
  return apiFetch(`/documents-session/${documentId}/signature/apercu`);
}

export function envoyerSignatureAttestation(documentId, { sujet, texte } = {}) {
  return apiFetch(`/documents-session/${documentId}/signature/envoyer`, {
    method: 'POST',
    body: JSON.stringify({ sujet, texte }),
  });
}

/**
 * Envoie les liens d'émargement d'une case, à la demande : `cible` vaut
 * 'participants' (page Feuille de présence) ou 'formateur' (menu Formateurs) ;
 * `jour` ('aaaa-mm-jj', une journée de la feuille) et `demiJournee` ('matin'
 * ou 'apres_midi') désignent la case que le lien ouvre. Sans jour, le serveur
 * prend la journée du jour ou la prochaine. `toutes` : le lien ouvre la ligne
 * du signataire avec toutes les journées et demi-journées à signer, et
 * `jour` / `demiJournee` ne servent plus.
 */
export function envoyerLiensEmargement(
  sessionId,
  cible = 'participants',
  { jour = null, demiJournee = 'matin', toutes = false, sujet, texte } = {}
) {
  return apiFetch(`/sessions-formation/${sessionId}/emargement/envoyer`, {
    method: 'POST',
    body: JSON.stringify({ cible, jour, demiJournee, toutes, sujet, texte }),
  });
}

/** Le gabarit du lien d'émargement tel qu'il partirait (balises comprises). */
export function apercuLiensEmargement(
  sessionId,
  cible = 'participants',
  { jour = null, demiJournee = 'matin', toutes = false } = {}
) {
  const query = new URLSearchParams({ cible, demiJournee });
  if (jour) query.set('jour', jour);
  if (toutes) query.set('toutes', 'true');
  return apiFetch(`/sessions-formation/${sessionId}/emargement/envoyer/apercu?${query}`);
}

/** Les pastilles de la barre latérale : ce qui attend un geste, par page. */
export function getNotifications() {
  return apiFetch('/notifications');
}

/** Ouvrir une liste la marque vue : sa pastille ne compte plus que ce qui arrive ensuite. */
export function marquerPastillesVues(sections) {
  return apiFetch('/notifications/vues', { method: 'POST', body: JSON.stringify({ sections }) });
}

/**
 * La cloche : ce que les clients ont fait — devis ou contrat signé, devis
 * refusé, pièce déposée. Les non vus d'abord, puis trente jours.
 */
export function getEvenementsClients() {
  return apiFetch('/notifications/clients');
}

/** Marque vus les gestes des clients : tous, ou un seul ({ type, id }). */
export function marquerEvenementsVus(cible = {}) {
  return apiFetch('/notifications/clients/vu', {
    method: 'POST',
    body: JSON.stringify(cible),
  });
}

/**
 * La cloche, section « À surveiller » : ce qui approche ou ce qui manque —
 * formations proches, planifications à faire, contrats non signés, élus sans
 * email, feuilles incomplètes. Tenue à jour par le passage quotidien.
 */
export function getAlertes() {
  return apiFetch('/notifications/alertes');
}

/** Marque vues les alertes : toutes, ou une seule ({ id }). */
export function marquerAlertesVues(cible = {}) {
  return apiFetch('/notifications/alertes/vu', {
    method: 'POST',
    body: JSON.stringify(cible),
  });
}

/** Cases déjà émargées d’une feuille de présence, tracé compris. */
/**
 * Corrige une feuille de présence : le formateur (nom et adresse) et la
 * qualité des participants. Le serveur refuse (409) dès que la feuille a été
 * envoyée — tant que personne ne l'a reçue, rien n'est figé.
 */
export function modifierFeuille(documentId, data) {
  return apiFetch(`/documents-session/${documentId}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export function getEmargements(documentId) {
  return apiFetch(`/documents-session/${documentId}/emargements`);
}

/**
 * Toutes les signatures reçues depuis les liens publics, toutes feuilles
 * confondues (vue « Signatures reçues » du menu Clients).
 */
export function getSignaturesRecues(params = {}) {
  const query = new URLSearchParams();
  for (const [cle, valeur] of Object.entries(params)) {
    if (valeur !== undefined && valeur !== null && valeur !== '') query.set(cle, valeur);
  }
  const s = query.toString();
  return apiFetch(`/documents-session/signatures-recues${s ? `?${s}` : ''}`);
}

/** Signe une case : un participant, une journée, matin ou après-midi, un tracé (PNG en data URL). */
export function signerEmargement(documentId, { participantId, jour, demiJournee = 'matin', image }) {
  return apiFetch(`/documents-session/${documentId}/emargements`, {
    method: 'POST',
    body: JSON.stringify({ participantId, jour, demiJournee, image }),
  });
}

/** Efface une case émargée par erreur. */
export function supprimerEmargement(documentId, participantId, jour, demiJournee = 'matin') {
  return apiFetch(
    `/documents-session/${documentId}/emargements/${participantId}/${jour}?demi=${encodeURIComponent(
      demiJournee
    )}`,
    { method: 'DELETE' }
  );
}

/**
 * URL du PDF. L'option apercu le sert en ligne (Content-Disposition: inline)
 * pour l'afficher dans un cadre ou un onglet ; sans elle, le navigateur
 * enregistre.
 */
export function urlPdfDocument(id, { forcer = false, apercu = false } = {}) {
  const base = process.env.REACT_APP_API_URL || '/api';
  const params = [];
  if (forcer) params.push('forcer=1');
  if (apercu) params.push('apercu=1');
  return `${base}/documents-session/${id}/pdf${params.length ? `?${params.join('&')}` : ''}`;
}

/** Nom de fichier annoncé par le serveur dans Content-Disposition. */
function nomDepuisEntete(entete, defaut) {
  const trouve = /filename="?([^"]+)"?/.exec(entete || '');
  return trouve ? trouve[1] : defaut;
}

/**
 * Télécharge le PDF d'un document et déclenche l'enregistrement.
 * Même mécanique que le devis et le contrat : fetch plutôt qu'un lien, pour
 * savoir quand le rendu est terminé et remonter une erreur lisible.
 */
export async function telechargerPdfDocument(id, { forcer = false } = {}) {
  const res = await fetch(urlPdfDocument(id, { forcer }), { credentials: 'include' });

  if (!res.ok) {
    let message = `Téléchargement impossible (${res.status})`;
    try {
      const corps = await res.json();
      if (corps?.message) message = corps.message;
    } catch {
      /* réponse non JSON : on garde le code HTTP */
    }
    const err = new Error(message);
    err.status = res.status;
    throw err;
  }

  const nom = nomDepuisEntete(res.headers.get('Content-Disposition'), `document-${id}.pdf`);
  const blob = await res.blob();

  const url = URL.createObjectURL(blob);
  const lien = document.createElement('a');
  lien.href = url;
  lien.download = nom;
  document.body.appendChild(lien);
  lien.click();
  lien.remove();
  URL.revokeObjectURL(url);

  return nom;
}

/** Les paramètres vides ne sont pas transmis : le serveur applique ses défauts. */
function versQuery(params) {
  const query = new URLSearchParams();
  for (const [cle, valeur] of Object.entries(params)) {
    if (valeur !== undefined && valeur !== null && valeur !== '') query.set(cle, valeur);
  }
  const s = query.toString();
  return s ? `?${s}` : '';
}

/**
 * Documents de session générés, paginés, filtrés par type
 * ('convocation' ou 'feuille_presence').
 */
export function getDocuments(params = {}) {
  return apiFetch(`/documents-session${versQuery(params)}`);
}

/** Sessions planifiées et délais de rappel configurés (page Rappel). */
export function getSessionsARappeler(params = {}) {
  return apiFetch(`/sessions-formation${versQuery(params)}`);
}



/** Refait le PDF d'un document de séance (bouton « Régénérer le PDF » de la fiche). */
export function regenererPdfDocument(documentId) {
  return apiFetch(`/documents-session/${documentId}/pdf/regenerer`, { method: 'POST' });
}
